import React, { useEffect, useState } from "react";
import Input from "../Inputs/Input/Input";
import InputCheckbox from "../Inputs/InputCheckbox/InputCheckbox";
import InputDropdown from "../../inputs/InputDropdown/InputDropdown";
import InputTextarea from "../Inputs/InputTextarea/InputTextarea";
import InputDocument from "../Inputs/InputDocument/InputDocument";
import InputRadioGroup from "../Inputs/InputRadioGroup/InputRadioGroup";
import type { FormElement, FormGroup, Option } from "../../../types/types";
import "./FormModal.scss";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import InputDropdownFilter from "../Inputs/InputDropdownFilter/InputDropdownFilter";
import InputDate from "../Inputs/InputDate/InputDate";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";

interface FormComponentProps {
    dynamicFormElements: FormGroup[];
    modalData: { [key: string]: any };
    associatedOptions: { [key: string]: any[] };
    handleInputChange: (name: string, value: any) => void;
    missingNames?: string[];
}

const FormComponent: React.FC<FormComponentProps> = ({
    dynamicFormElements,
    modalData,
    associatedOptions,
    handleInputChange,
    missingNames = [],
}) => {
    const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0);

    const isElementVisible = (element: FormElement): boolean => {
        if (!element.visible) return false;

        if (element.visibleWhen) {
            const conditions = Array.isArray(element.visibleWhen)
                ? element.visibleWhen
                : [element.visibleWhen];

            return conditions.every(({ field, value: expectedValue }) => {
                const fieldValue = modalData[field];
                if (Array.isArray(fieldValue)) {
                    return fieldValue.includes(expectedValue);
                }
                return String(fieldValue ?? "") === String(expectedValue ?? "");
            });
        }

        return true;
    };

    const visibleGroups = dynamicFormElements.filter((group: FormGroup) =>
        group.elements.some((element: FormElement) =>
            isElementVisible(element)
        )
    );

    const totalGroups = visibleGroups.length;
    const currentGroup = visibleGroups[currentGroupIndex];

    const handleNext = (): void => {
        setCurrentGroupIndex((prev: number) =>
            Math.min(prev + 1, totalGroups - 1)
        );
    };

    const handlePrevious = (): void => {
        setCurrentGroupIndex((prev: number) => Math.max(prev - 1, 0));
    };

    // Al fallar el guardado, saltar al primer grupo con algun campo pendiente:
    // el aviso los nombra todos, pero el resaltado solo se ve en el grupo
    // visible, y en un formulario por pasos el campo puede estar en otro.
    useEffect(() => {
        if (missingNames.length === 0) return;

        const groupIndex = visibleGroups.findIndex((group: FormGroup) =>
            group.elements.some(
                (element: FormElement) =>
                    isElementVisible(element) &&
                    missingNames.includes(element.name)
            )
        );

        if (groupIndex >= 0) setCurrentGroupIndex(groupIndex);
    }, [missingNames]);

    if (!currentGroup) return null;

    const findOption = (val: any, options: Option[]): Option | undefined => {
        if (val == null || val === "") return undefined;

        if (typeof val === "object") {
            const candidate = val.id ?? val.value;

            return (
                options.find((o) => String(o.value) === String(candidate)) ||
                options.find((o) => String(o.label) === String(val.label))
            );
        }

        return (
            options.find((o) => String(o.value) === String(val)) ||
            options.find((o) => String(o.label) === String(val))
        );
    };

    const findMultipleOptions = (val: any, options: Option[]): Option[] => {
        if (val == null || val === "") return [];

        const values = Array.isArray(val) ? val : [val];

        return values
            .map((item) => findOption(item, options))
            .filter(Boolean) as Option[];
    };

    return (
        <form className="modal-form">
            <div className="form-group">
                {currentGroup.titleVisible && (
                    <div className="form-group-title">
                        {totalGroups > 1 && (
                            <button
                                type="button"
                                onClick={handlePrevious}
                                className="navigation-button left"
                                disabled={currentGroupIndex === 0}
                            >
                                {IoIosArrowBack({ size: 16 })}
                            </button>
                        )}
                        <h3>{currentGroup.title}</h3>
                        {totalGroups > 1 && (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="navigation-button right"
                                disabled={currentGroupIndex === totalGroups - 1}
                            >
                                {IoIosArrowForward({ size: 16 })}
                            </button>
                        )}
                    </div>
                )}
                <div className="form-group-elements modal-form-wrapper">
                    {currentGroup.elements
                        .filter((element: FormElement) =>
                            isElementVisible(element)
                        )
                        .map((elementoBase: FormElement) => {
                            /* La etiqueta puede depender de otro campo del propio
                               formulario (p. ej. la unidad del peso depende del
                               tipo de residuo elegido). Se resuelve aquí para que
                               el resto del render siga usando `element.label`. */
                            const element: FormElement =
                                elementoBase.labelDinamica
                                    ? {
                                          ...elementoBase,
                                          label: elementoBase.labelDinamica(
                                              modalData
                                          ),
                                      }
                                    : elementoBase;

                            let value = element.defaultValue ?? "";
                            value = modalData[element.name] ?? value;

                            let selectedOption = null;
                            let options: {
                                value: string;
                                label: string;
                                id?: number | undefined;
                                fatherId?: number | undefined;
                                associatedField?: string;
                                valueField?: string;
                            }[] = [];

                            if (element.type === "select") {
                                value =
                                    element.options?.find(
                                        (x) =>
                                            x.id ===
                                                (modalData[
                                                    x.associatedField ?? ""
                                                ] === null ||
                                                modalData[
                                                    x.associatedField ?? ""
                                                ]?.id === undefined
                                                    ? modalData[
                                                          x.associatedField ??
                                                              ""
                                                      ]
                                                    : modalData[
                                                          x.associatedField ??
                                                              ""
                                                      ]?.id) || ""
                                    )?.value || "";

                                options = element.associatedFormElement
                                    ? associatedOptions[element.name] || []
                                    : element.options || [];

                                const rawValue = modalData[element.name];

                                const findOption = (
                                    val: any
                                ): Option | undefined => {
                                    if (val == null) return undefined;
                                    // Si es objeto con id o value
                                    if (
                                        typeof val === "object" &&
                                        (val.id || val.value)
                                    ) {
                                        const id = val.id ?? val.value;
                                        return options.find(
                                            (o) =>
                                                String(o.value) === String(id)
                                        );
                                    }
                                    // Primitivo: intentar value o label
                                    return (
                                        options.find(
                                            (o) =>
                                                String(o.value) === String(val)
                                        ) ||
                                        options.find(
                                            (o) =>
                                                String(o.label) === String(val)
                                        ) ||
                                        options.find(
                                            (o) => String(o.id) === String(val)
                                        )
                                    );
                                };
                                selectedOption = findOption(rawValue) ?? null;
                            }

                            const dynamicOptions =
                                element.type === "select" &&
                                associatedOptions[element.name]
                                    ? associatedOptions[element.name]
                                    : [];

                            const resolvedOptions =
                                Array.isArray(options) && options.length > 0
                                    ? options
                                    : Array.isArray(element.options) &&
                                        element.options.length > 0
                                      ? element.options
                                      : [];

                            switch (element.type) {
                                case "section":
                                    return (
                                        <div
                                            key={element.name}
                                            className="form-section-title"
                                        >
                                            {element.label}
                                        </div>
                                    );
                                case "text":
                                case "email":
                                case "password":
                                case "number":
                                case "tel":
                                    return (
                                        <Input
                                            key={element.name}
                                            type={element.type}
                                            label={element.label}
                                            placeholder={element.placeholder}
                                            value={value}
                                            onChange={(
                                                value: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleInputChange(
                                                    element.name,
                                                    value.target.value
                                                )
                                            }
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "checkbox":
                                    return (
                                        <InputCheckbox
                                            key={element.name}
                                            id={element.name}
                                            label={element.label}
                                            checked={Boolean(value)}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                handleInputChange(
                                                    element.name,
                                                    e.target.checked
                                                );

                                                if (
                                                    e.target.checked &&
                                                    element.exclusiveGroup
                                                ) {
                                                    dynamicFormElements
                                                        .flatMap(
                                                            (group) =>
                                                                group.elements
                                                        )
                                                        .filter(
                                                            (other) =>
                                                                other.exclusiveGroup ===
                                                                    element.exclusiveGroup &&
                                                                other.name !==
                                                                    element.name
                                                        )
                                                        .forEach((other) => {
                                                            handleInputChange(
                                                                other.name,
                                                                false
                                                            );
                                                            if (
                                                                other.valueMapField
                                                            ) {
                                                                handleInputChange(
                                                                    other.valueMapField,
                                                                    null
                                                                );
                                                            }
                                                        });
                                                }

                                                if (
                                                    e.target.checked &&
                                                    element.valueMapField
                                                ) {
                                                    handleInputChange(
                                                        element.valueMapField,
                                                        element.valueWhenChecked
                                                    );
                                                } else if (
                                                    !e.target.checked &&
                                                    element.valueMapField
                                                ) {
                                                    handleInputChange(
                                                        element.valueMapField,
                                                        null
                                                    );
                                                }
                                            }}
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "select": {
                                    const resolvedOptions =
                                        element.associatedFormElement
                                            ? associatedOptions[element.name] ||
                                              []
                                            : element.options || [];

                                    const rawValue =
                                        modalData[element.name] ??
                                        element.defaultValue ??
                                        null;

                                    const selectedOption = findOption(
                                        rawValue,
                                        resolvedOptions
                                    );

                                    return (
                                        <InputDropdown_New
                                            key={`${element.name}-${resolvedOptions.length}-${String(rawValue ?? "")}`}
                                            label={element.label}
                                            options={resolvedOptions}
                                            placeholder={element.placeholder}
                                            defaultValue={
                                                selectedOption ?? null
                                            }
                                            onChange={(opt) => {
                                                const newValue = Array.isArray(
                                                    opt
                                                )
                                                    ? opt.map((option) =>
                                                          typeof option ===
                                                              "object" &&
                                                          option !== null &&
                                                          "value" in option
                                                              ? option.value
                                                              : option
                                                      )
                                                    : typeof opt === "object" &&
                                                        opt !== null &&
                                                        "value" in opt
                                                      ? opt.value
                                                      : opt;

                                                handleInputChange(
                                                    element.name,
                                                    newValue
                                                );
                                            }}
                                            multiple={
                                                element.withCheckbox || false
                                            }
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                }

                                case "multiselect": {
                                    const resolvedOptions =
                                        element.associatedFormElement
                                            ? associatedOptions[element.name] ||
                                              []
                                            : element.options || [];

                                    const rawValue =
                                        modalData[element.name] ??
                                        element.defaultValue ??
                                        [];

                                    const selectedOptions = findMultipleOptions(
                                        rawValue,
                                        resolvedOptions
                                    );

                                    return (
                                        <InputDropdown_New
                                            key={`${element.name}-${resolvedOptions.length}`}
                                            label={element.label}
                                            options={resolvedOptions}
                                            defaultValue={selectedOptions}
                                            placeholder={element.placeholder}
                                            searchable={true}
                                            multiple={true}
                                            returnFormat="value"
                                            onChange={(selected) => {
                                                const newValue = Array.isArray(
                                                    selected
                                                )
                                                    ? selected.map((option) =>
                                                          typeof option ===
                                                              "object" &&
                                                          option !== null &&
                                                          "value" in option
                                                              ? option.value
                                                              : option
                                                      )
                                                    : selected == null
                                                      ? []
                                                      : typeof selected ===
                                                              "object" &&
                                                          "value" in selected
                                                        ? [selected.value]
                                                        : [selected];

                                                handleInputChange(
                                                    element.name,
                                                    newValue
                                                );
                                            }}
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                }
                                case "textarea":
                                    return (
                                        <InputTextarea
                                            key={element.name}
                                            label={element.label}
                                            placeholder={element.placeholder}
                                            value={value}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLTextAreaElement>
                                            ) =>
                                                handleInputChange(
                                                    element.name,
                                                    e.currentTarget.value
                                                )
                                            }
                                            required={element.required}
                                            disabled={element.disabled}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "document": {
                                    const documentValue =
                                        modalData[element.name] ??
                                        element.defaultValue ??
                                        null;

                                    const existingDocumentName =
                                        typeof documentValue === "string"
                                            ? documentValue
                                            : (documentValue?.name ??
                                              documentValue?.fileName ??
                                              documentValue?.filename ??
                                              documentValue?.originalName ??
                                              documentValue?.url ??
                                              "");

                                    return (
                                        <InputDocument
                                            key={`${element.name}-${existingDocumentName}`}
                                            existingDocumentName={
                                                existingDocumentName
                                            }
                                            onFileChange={(file: File | null) =>
                                                handleInputChange(
                                                    element.name,
                                                    file
                                                )
                                            }
                                            label={element.label}
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                }
                                case "radio":
                                    return (
                                        <InputRadioGroup
                                            key={element.name}
                                            name={element.name}
                                            options={element.options!.map(
                                                (group: any) => ({
                                                    label: group.label,
                                                    options: Array.isArray(
                                                        group.options
                                                    )
                                                        ? group.options.map(
                                                              (opt: any) => ({
                                                                  value: String(
                                                                      opt.value
                                                                  ),
                                                                  label: opt.label,
                                                              })
                                                          )
                                                        : [],
                                                })
                                            )}
                                            selectedValue={value}
                                            onChange={(value: any) =>
                                                handleInputChange(
                                                    element.name,
                                                    value
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                case "date":
                                    const dateValue = value
                                        ? String(value).slice(0, 10)
                                        : "";
                                    return (
                                        <InputDate
                                            key={element.name}
                                            label={element.label}
                                            name={element.name}
                                            value={dateValue}
                                            onChange={(date: any) =>
                                                handleInputChange(
                                                    element.name,
                                                    date.target.value
                                                )
                                            }
                                            required={element.required}
                                            className={
                                                missingNames.includes(
                                                    element.name
                                                )
                                                    ? "input-error"
                                                    : ""
                                            }
                                        />
                                    );
                                default:
                                    return (
                                        <div key={element.name}>
                                            <strong>
                                                Unsupported element:{" "}
                                            </strong>
                                            {element.type}
                                        </div>
                                    );
                            }
                        })}
                </div>
            </div>
        </form>
    );
};

export default FormComponent;
